<script setup lang="ts">
import { $inj, $injByInterface } from "@/common/decorators/depinject";
import {
  TrackerFactory,
  WorkflowFactory,
  WorkspaceStateFactory,
  BroadcastService,
  NotificationFactory,
  GAnalyticsFactory,
  ApplicantFactory,
  EnrollmentFactory,
  WorkspaceFactory,
} from "@/common/services/services.module";
import {useRoute, type RouteLocationNormalizedLoaded, RouteLocationNormalized} from "vue-router";
import router from "@/open/router";
import { WorkflowStatesConstant } from "@/common/constant/WorkflowStatesConstant";
import type IWorkspaceMetadataStore from "@/common/services/Workspace/IWorkspaceMetadataStore";
import type IWorkspaceStore from "@/common/services/Workspace/IWorkspaceStore";
import OpenAuthFactory from "@/open/app/factories/authorization/OpenAuthFactory";
import {computed, onErrorCaptured, watch, onMounted, onUnmounted} from 'vue'
import { BroadcastConstant } from "@/common/constant/BroadcastConstant";
import { WorkflowConstant } from "@/common/constant/WorkflowConstant";
import { PagetransitionConstant } from "@/common/constant/PagetransitionConstant";
import { ErrorHandler } from "@/open/handlers/ErrorHandler";
import type IWorkspace from "@/common/models/IWorkspace";
import IDScanIDVCService from '@/common/services/Vendor/IDScan/IDScanIDVC.service'
import ActiveUserKeepAliveService from '@/common/services/KeepaliveService/ActiveUserKeepAliveService'
import TokenTimeoutService from '@/open/services/TokenTimeoutService'

const route = useRoute();
const IGNORED_STATES = [WorkflowStatesConstant.ERROR, WorkflowStatesConstant.CONGRATULATIONS];
let currentInstantStateId = -1;

const activeUserKeepAliveService = $inj(ActiveUserKeepAliveService);
const tokenTimeoutService = $inj(TokenTimeoutService);

onMounted(() => {
  tokenTimeoutService.trackTimeout()
  activeUserKeepAliveService.startListening()
})

onUnmounted(() => {
  activeUserKeepAliveService.stopListening()
  tokenTimeoutService.removeTimeout()
})

const helpers = {
  isWorkflowDescendant: (currentRoute: RouteLocationNormalizedLoaded) => {
    return (
      currentRoute.fullPath.includes(WorkflowStatesConstant.WORKFLOW.STATE) &&
      currentRoute.name !== WorkflowStatesConstant.INTERIM.STATE
    );
  },
  isApplicantStateDescendant: (currentRoute: RouteLocationNormalizedLoaded) => {
    const findIndex = (currentRoute.matched||[]).findIndex(m=>m.name === WorkflowStatesConstant.APPLICANT.STATE);
    return findIndex > -1;
  },

  isWorkflow: (currentRoute: RouteLocationNormalizedLoaded) => {
    return currentRoute.name === WorkflowStatesConstant.WORKFLOW.STATE;
  },
  handleError: async (err:any) => {
    ErrorHandler.handleNavigationErrorForWorkspace((await actions.WorkspaceUUID()).workspaceUUID, await actions.TrackerUUID());
  },
  updateWorkspaceStore: (
    workspaceStore: IWorkspaceStore,
    data: { trackerUUID: string; workspace: IWorkspace; currentState: any; workflowUUID: string }
  ) => {
    workspaceStore.trackerUUID = data.trackerUUID;
    workspaceStore.workspaceUUID = data.workspace.workspaceUUID;
    workspaceStore.workspaceType = data.workspace.workspaceType;
    workspaceStore.workflow.currentState = data.currentState;
    workspaceStore.workflow.workflowUUID = data.workflowUUID;
    workspaceStore.workspace = data.workspace;
  },
  updateWorkspaceMetadataStore: (
    workspaceMetadataStore: IWorkspaceMetadataStore,
    data: { plaidLinkToken: any; receivedRedirectUri: any }
  ) => {
    workspaceMetadataStore.plaidlinktoken = data.plaidLinkToken;
    workspaceMetadataStore.receivedredirecturi = data.receivedRedirectUri;
  }
};

const actions = {
  Authorize: function (openAuthFactory: OpenAuthFactory) {
    return openAuthFactory.authorize(route.params.workspaceUUID as string);
  },
  WorkspaceUUID: function (): Promise<IWorkspace> {
    return $inj(WorkspaceFactory).getWorkspace(route.params.workspaceUUID as string)
  },
  TrackerUUID: function () {
    return Promise.resolve(route.query.trackerUUID as string);
  },
  Tracker: function (workspaceUUID: string, trackerUUID: string, trackerFactory: TrackerFactory) {
    return trackerFactory.get(workspaceUUID, trackerUUID);
  },
  CurrentState: function (tracker: { state: string; }, workflowFactory: WorkflowFactory) {
    return workflowFactory.setState(tracker.state);
  },
  GAnalytics: function (gAnalytics: GAnalyticsFactory, WorkspaceUUID: string) {
    return gAnalytics.init(WorkspaceUUID);
  },
  ReceivedRedirectUri: function () {
    return Promise.resolve(route.query.receivedRedirectUri);
  },
  PlaidLinkToken: function () {
    return Promise.resolve(route.query.plaidLinkToken);
  }
};

const OnSuccess = async function (to: RouteLocationNormalized | undefined) {
  const workflowFactory = $inj(WorkflowFactory);
  const workspaceStateFactory = $inj(WorkspaceStateFactory);
  const gAnalyticsFactory = $inj(GAnalyticsFactory);
  const enrollmentFactory = $inj(EnrollmentFactory);
  const applicantFactory = $inj(ApplicantFactory);
  const broadcastService = $inj(BroadcastService);
  const isPreviousNavigation = workspaceStore.workflow.direction === WorkflowConstant.PREVIOUS;

  if (helpers.isWorkflowDescendant(router.currentRoute.value)) {
    notificationFactory.clearMessages();
    const currentState = workflowFactory.getState();

    /**Ignored states like END state doesn't need tracker update */
    if (IGNORED_STATES.indexOf(workflowFactory.getState().state) === -1
      && currentState.stateId !== currentInstantStateId
      ) {
      /**tracker update needed here only for forward navigation.
       * Back navigation is already handled in workflowfactory.previous() */
      if(!isPreviousNavigation){
        await workspaceStateFactory.updateWorkspaceState(currentState.stateId).then(() => {
          broadcastService.broadcast(BroadcastConstant.WORKFLOW_PREVIOUS_STATE_UPDATED, null);
        });
      }
      else{
        broadcastService.broadcast(BroadcastConstant.WORKFLOW_PREVIOUS_STATE_UPDATED, null);
      }
      gAnalyticsFactory.sendWorkflowEvent(workflowFactory.getState());
    }
    //to prevent duplicate tracker updates for same state in consecutive
    currentInstantStateId = currentState.stateId;
  }

  if (
      to
      && isChildOf(to, WorkflowStatesConstant.ENROLLMENT.STATE)
      && workspaceStore.enrollment.enrollmentId
  ) {
    await enrollmentFactory.updateEnrollmentState(
        route.params.workspaceUUID as string,
        workspaceStore.enrollment.enrollmentId,
        router.currentRoute.value.name
    );
  } else if (
      to
      && isChildOf(to, WorkflowStatesConstant.APPLICANT.STATE)
      && workspaceStore.activeApplicantId
  ) {
    await applicantFactory.updateApplicantState(
        route.params.workspaceUUID as string,
        workspaceStore.activeApplicantId,
        router.currentRoute.value.name
    );
  }

  workflowFactory.setEndPreloadingStatus();
  if (helpers.isWorkflow(router.currentRoute.value)) {
    await workflowFactory.init();
  }

  function isChildOf(to: RouteLocationNormalized, stateName: string) {
    return to.matched.some(state => state.name == stateName)
  }
};
watch(route, OnSuccess);


onMounted(async () => {

  const gAnalyticsFactory = $inj(GAnalyticsFactory);
  const idScanIDVCService = $inj(IDScanIDVCService);

  // todo(mikol): Only prefetch resources applicable to the current workflow.
  idScanIDVCService.preload()
  const workspace = await actions.WorkspaceUUID();
  await actions.GAnalytics(gAnalyticsFactory, workspace.workspaceUUID);
})

const openAuthFactory = $inj(OpenAuthFactory);
const trackerFactory = $inj(TrackerFactory);
const workflowFactory = $inj(WorkflowFactory);
const notificationFactory = $inj(NotificationFactory);
const workspaceStore = $injByInterface<IWorkspaceStore>("IWorkspaceStore");
const workspaceMetadataStore = $injByInterface<IWorkspaceMetadataStore>("IWorkspaceMetadataStore");

try {
  await actions.Authorize(openAuthFactory);
  const workspace = await actions.WorkspaceUUID();
  const trackerUUID = await actions.TrackerUUID();
  const tracker = await actions.Tracker(workspace.workspaceUUID, trackerUUID, trackerFactory);
  helpers.updateWorkspaceStore(workspaceStore, {
    trackerUUID,
    workspace: workspace,
    currentState: tracker.state,
    workflowUUID: tracker.workflow
  });

  await actions.CurrentState(tracker, workflowFactory);
  const plaidLinkToken = await actions.PlaidLinkToken();
  const receivedRedirectUri = await actions.ReceivedRedirectUri();

  helpers.updateWorkspaceMetadataStore(workspaceMetadataStore, {
    plaidLinkToken,
    receivedRedirectUri
  });

  await OnSuccess();
} catch (error) {
  //this block is to handle error thrown within this parent component for above code.
  //any error from children components will be caught in onErrorCaptured hook
  helpers.handleError(error);
}
const transition = computed((): string => {
  const state = workspaceStore.workflow.direction === WorkflowConstant.NEXT
    ?PagetransitionConstant.FORWARD
    :PagetransitionConstant.BACKWARD;
  return state;
});

onErrorCaptured((err, vm, info) => {
  //note: an error "OPEN_NOT_AUTHORIZED" is expected when Tracker() called.
  //This happens when you reload the URL in browser "/workflow/workspaceUUID?trackerUUID"
  //In this case we redirect either to continue or error page
  helpers.handleError(err);
  //prevent from further propagation above
  return false;
});
</script>

<template>
  <blt-wizard>
    <router-view v-slot="{ Component }">
      <transition :name="transition" :duration="{ enter: 1000, leave: 1000 }">
        <component :is="Component" />
      </transition>
    </router-view>
  </blt-wizard>

<!--  <inactivity-modal :timer="timeoutWarningThresholdSeconds"/>-->
</template>
